import {
  GoabButton,
  GoabCheckbox,
  GoabCheckboxOnChangeDetail,
  GoabDivider,
  GoabFormItem,
  GoabInput,
  GoabLink,
  GoabRadioGroup,
  GoabRadioGroupOnChangeDetail,
  GoabRadioItem,
  GoabText,
  GoaInputOnBlurDetail,
} from '@abgov/angular-components';
import {
  ChangeDirectionType,
  PreventFocusScrollDirective,
  RadioItemHtmlLabelDirective,
} from '@ala/common-ui';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CapacityReportStatus } from '@libs/services/graphql/generated/ala-backend.service';
import { Subject, takeUntil } from 'rxjs';
import { DataEntryStateService } from '../../../../services/data-entry-state.service';
import { ReportingPeriodPipe } from '../../../../shared/pipes/reporting-period.pipe';
import {
  BedCountChangeEventArgs,
  BedCountTypes,
} from '../../viewmodels/bed-count-change-event-args';
import { IDataEntryRow } from '../../viewmodels/data-entry-row.viewmodel';
import { IDataEntry } from '../../viewmodels/data-entry.viewmodel';
import { DataEntryRowComponent } from '../data-entry-row/data-entry-row.component';
import { ReasonsForChangeComponent } from '../reasons-for-change/reasons-for-change.component';

@Component({
  selector: 'app-data-entry',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DataEntryRowComponent,
    GoabInput,
    GoabText,
    GoabLink,
    RouterLink,
    GoabRadioGroup,
    GoabRadioItem,
    GoabDivider,
    GoabFormItem,
    GoabCheckbox,
    GoabButton,
    ReportingPeriodPipe,
    ReasonsForChangeComponent,
    PreventFocusScrollDirective,
    RadioItemHtmlLabelDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './data-entry.component.html',
  styleUrl: './data-entry.component.scss',
})
export class DataEntryComponent implements AfterViewInit, OnDestroy {
  @ViewChild(DataEntryRowComponent) dataEntryRow!: DataEntryRowComponent;
  dataEntryStateService = inject(DataEntryStateService);
  private elementRef = inject(ElementRef);

  private destroy$ = new Subject<void>();

  dataEntry = input.required<IDataEntry | undefined>();
  fieldToFocus = input<{ row: number; col: number } | null>(null); // the field to focus on after a change
  private internalFieldToFocus = signal<{ row: number; col: number } | null>(
    null
  );
  localFieldToFocus = computed(() => {
    // Prioritize parent's fieldToFocus, fall back to internal
    return this.fieldToFocus() || this.internalFieldToFocus();
  });

  dataEntryRowChange = output<BedCountChangeEventArgs>();
  reasonsForChange = output<string[]>();
  otherReasonForChange = output<string>();
  submitted = output<{
    areReasonsForChangeEnabled: boolean;
    isOtherReasonEnabled: boolean;
    facilityHasSmokingPolicy: boolean;
  }>();
  saveLater = output<void>();

  isDataChangedSignal = signal(false); // the state of editing the data entry fields
  changingCountLength = signal<number | undefined>(undefined); // tracks the number of characters in the currently-changing data entry field
  changingOtherReasonLength = signal<number | undefined>(undefined); // tracks the number of characters in the currently-changing Other Reason input field
  facilitySmokingPolicy = signal<boolean | undefined>(undefined);
  smokingPolicyChanged = signal(false); // tracks if smoking policy has been changed from initial value

  readonly touched = computed((): boolean => {
    return (
      this.isDataChangedSignal() ||
      this.changingCountLength() !== undefined ||
      this.changingOtherReasonLength() !== undefined ||
      this.smokingPolicyChanged()
    );
  });
  readonly reportStatus = computed((): CapacityReportStatus => {
    // If user is currently typing/changing something, always show INPROGRESS
    if (
      this.changingCountLength() !== undefined ||
      this.changingOtherReasonLength() !== undefined
    ) {
      return CapacityReportStatus.INPROGRESS;
    }

    // If any data has been touched/changed, show INPROGRESS
    if (this.touched()) {
      return CapacityReportStatus.INPROGRESS;
    }

    // Otherwise, use the status from dataEntry if available
    const entry = this.dataEntry();
    if (entry) {
      return entry.status;
    }

    return CapacityReportStatus.NOTSTARTED;
  });

  // isInitialDataExistSignal will only be false before the very first data are entered for the facility
  private readonly isInitialDataExistSignal = computed((): boolean => {
    const entry = this.dataEntry();
    if (entry) {
      return entry.dataEntryRows.some(
        (row) => row.operationalBeds || row.occupiedBeds
      );
    }
    return false;
  });

  readonly rowsWithFundedBeds = computed((): IDataEntryRow[] => {
    const entry = this.dataEntry();
    if (entry) {
      return entry.dataEntryRows.filter((row) => row.fundedBeds > 0);
    }
    return [];
  });

  readonly reasonsForChangeEnabled = computed(() =>
    this.rowsWithFundedBeds().some(
      (row) =>
        row.previousOperationalBeds !== undefined &&
        row.operationalBeds !== row.previousOperationalBeds
    )
  );

  // the state of the No-Changes checkbox
  private userCheckedNoChanges = signal(false);
  readonly chkNoChangesStateSignal = computed(() => {
    const entry = this.dataEntry();
    const isInitialDataExist = this.isInitialDataExistSignal();

    if (!entry) {
      return {
        visible: false,
        enabled: false,
        checked: false,
      };
    }

    const visible =
      entry.status === CapacityReportStatus.INPROGRESS || isInitialDataExist;
    const enabled = entry.status === CapacityReportStatus.NOTSTARTED;
    const checked = this.userCheckedNoChanges();

    return {
      visible,
      enabled,
      checked,
    };
  });

  readonly isReadOnlySignal = computed((): boolean => {
    const entry = this.dataEntry();
    return entry ? entry.status === CapacityReportStatus.COMPLETED : false;
  });

  private nextField = signal<{ row: number; col: number } | undefined>(
    undefined
  ); // the field to focus on after a click

  private tabDirection: ChangeDirectionType | undefined = undefined; // the direction of the tabbing (1 for next, -1 for previous)

  bedTypeDescription = [
    '24/7 RN/RPN care for complex health needs not manageable at home.',
    'Home-like setting with 24/7 LPN and HCA care.',
    'Type B with added security features for safety.',
    '24/7 hospice care for individuals nearing end-of-life.',
  ];

  private isReasonsForChange = signal<boolean>(false);
  readonly isReasonsForChangesProvided = computed(() => {
    const entry = this.dataEntry();
    const currentReasonsProvided = this.isReasonsForChange();

    // If we have current reasons from the event, use those
    if (currentReasonsProvided) {
      return true;
    }

    // Otherwise, check the data entry for existing reasons
    return (
      (entry?.reasonsForChange && entry.reasonsForChange.length > 0) || false
    );
  });

  readonly reasonsNoOther = computed(() => {
    const entry = this.dataEntry();
    return entry
      ? (entry.reasonsForChange || []).filter((r) => r !== 'Other')
      : [];
  });
  readonly showOtherReasons = computed(() => {
    const entry = this.dataEntry();
    return entry ? entry.hasOtherReasonForChange || false : false;
  });

  readonly hasSmokingPolicy = computed(() => {
    const entry = this.dataEntry();
    if (entry) {
      return entry?.facilityHasSmokingPolicy;
    }
    return undefined;
  });

  readonly doShowOtherReason = computed(() => {
    const entry = this.dataEntry();
    return entry && entry.status === CapacityReportStatus.COMPLETED
      ? (entry.otherReasonForChange ?? '').trim().length > 0
      : false;
  });

  readonly doShowReasonsForChangeOnCompleted = computed(() => {
    return this.reasonsNoOther().length > 0 || this.doShowOtherReason();
  });

  private rowsValidationStatus = signal<Map<number, boolean>>(new Map());

  // Automatically uncheck NoChanges when data is changed
  private dataChangedEffect = effect(() => {
    if (this.isDataChangedSignal()) {
      this.userCheckedNoChanges.set(false);
    }
  });

  constructor() {
    this.dataEntryStateService.editorClosed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.userCheckedNoChanges.set(false);
        this.smokingPolicyChanged.set(false);
      });
  }

  ngAfterViewInit(): void {
    // TODO replace with waitForContent utility function
    const waitForContent = () => {
      const operationalFields = Array.from(
        this.elementRef.nativeElement.querySelectorAll('[id^="operational"]')
      );

      // Check if we have fields AND they're actually visible in viewport
      const hasVisibleFields = operationalFields.some((field) => {
        const rect = (field as Element).getBoundingClientRect();
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth
        );
      });

      if (hasVisibleFields) {
        this.onContentVisible();
      } else {
        // Check again next frame
        requestAnimationFrame(waitForContent);
      }
    };
    waitForContent();
    setTimeout(() => {
      const drawerContent = document
        .querySelector('goa-drawer')
        ?.shadowRoot?.querySelector('.scroll-content');
      if (drawerContent instanceof HTMLElement) {
        (drawerContent as HTMLElement).style.marginTop = '-1rem';
      }
    }, 100);
  }

  private onContentVisible(): void {
    this.internalFieldToFocus.set({ row: 0, col: 0 });
  }

  trackByRow(index: number, row: IDataEntryRow): string {
    return row.bedType.type;
  }

  onReasonsForChange(reasons: string[]) {
    this.isReasonsForChange.set(reasons.length > 0);
    this.reasonsForChange.emit(reasons);
  }

  onOtherReasonBlur($event: GoaInputOnBlurDetail) {
    const value = $event.value.trim();
    this.otherReasonForChange.emit(value);
  }

  onSmokingPolicyChange($event: GoabRadioGroupOnChangeDetail) {
    if ($event && $event.value) {
      const newValue = $event.value.toLowerCase() === 'true';
      const originalValue = this.hasSmokingPolicy(); // Get the original value from data entry

      // Set the new value
      this.facilitySmokingPolicy.set(newValue);

      // Mark as changed only if it's different from the original data entry value
      // If user changes back to original value, mark as not changed
      this.smokingPolicyChanged.set(originalValue !== newValue);
    }
  }

  private rowHasAllValues(row: IDataEntryRow): boolean {
    return ['operationalBeds', 'occupiedBeds'].every(
      (field: string) =>
        row[field as keyof IDataEntryRow] !== undefined &&
        !Number.isNaN(row[field as keyof IDataEntryRow] as number)
    );
  }

  readonly isSubmittable = computed((): boolean => {
    const isAnyRowInvalid = Array.from(
      this.rowsValidationStatus().values()
    ).some((hasError) => hasError);

    // For the other reason validation, we need to check if there's content regardless of completion status
    const entry = this.dataEntry();
    const hasOtherReasonContent = entry
      ? (entry.otherReasonForChange ?? '').trim().length > 0
      : false;

    console.log();
    return (
      !isAnyRowInvalid &&
      (this.hasSmokingPolicy() !== null ||
        this.facilitySmokingPolicy() !== undefined) &&
      ((this.reportStatus() === CapacityReportStatus.INPROGRESS &&
        this.rowsWithFundedBeds().every(this.rowHasAllValues) &&
        (!this.reasonsForChangeEnabled() ||
          (this.isReasonsForChangesProvided() &&
            (!this.showOtherReasons() ||
              (this.changingOtherReasonLength() === undefined &&
                hasOtherReasonContent) ||
              (this.changingOtherReasonLength() ?? 0) > 0)))) ||
        this.chkNoChangesStateSignal().checked)
    );
  });

  readonly isSaveLaterable = computed(
    (): boolean => this.touched() || this.reasonsForChangeEnabled()
  );

  // this event is raised when focus is moved from an input field after changing its value
  onDataEntryRowChange(payload: BedCountChangeEventArgs) {
    if (this.nextField()) {
      payload.nextFieldCoords = this.nextField();
    } else if (this.tabDirection !== undefined) {
      payload.nextFieldCoords =
        this.nextField() ||
        this.computeFieldToFocus(
          this.rowsWithFundedBeds().findIndex(
            (row: IDataEntryRow) =>
              row.bedType.type === payload.changedFieldCoords.bedType.type
          ),
          payload.changedFieldCoords.countType,
          this.tabDirection
        );
    } else {
      payload.nextFieldCoords = undefined;
    }

    this.dataEntryRowChange.emit(payload);
    this.isDataChangedSignal.set(true);
  }

  // this event is raised when focus is moved from an input field without changing the value
  onFieldChange(payload: BedCountChangeEventArgs) {
    if (this.nextField()) {
      payload.nextFieldCoords = this.nextField();
    } else if (this.tabDirection !== undefined) {
      payload.nextFieldCoords = this.computeFieldToFocus(
        this.rowsWithFundedBeds().findIndex(
          (row: IDataEntryRow) =>
            row.bedType.type === payload.changedFieldCoords.bedType.type
        ),
        payload.changedFieldCoords.countType,
        this.tabDirection
      );
    } else {
      payload.nextFieldCoords = undefined;
    }

    if (payload.nextFieldCoords) {
      this.internalFieldToFocus.set(payload.nextFieldCoords);
    }
  }

  onNoChangesCheckboxChange(event: GoabCheckboxOnChangeDetail) {
    this.userCheckedNoChanges.set(event.checked);
  }

  onSubmit() {
    this.submitted.emit({
      areReasonsForChangeEnabled: this.reasonsForChangeEnabled(),
      isOtherReasonEnabled: this.showOtherReasons(),
      facilityHasSmokingPolicy:
        this.hasSmokingPolicy() !== true
          ? this.facilitySmokingPolicy() ?? false
          : true, // the smoking policy should not be changed when selected YES already
    });
  }

  onSaveLater() {
    this.saveLater.emit();
  }

  // this happens when the user clicks on a input field
  onFieldSelection($event: { row: number; col: 'Operational' | 'Occupied' }) {
    if ($event && typeof $event.row === 'number' && 'col' in $event) {
      this.nextField.set({
        row: $event.row,
        col: BedCountTypes[$event.col],
      });
      setTimeout(() => {
        this.nextField.set(undefined);
      }, 150);
    }
  }

  // this happens when the user tabs to an input field
  onTabDirection(direction: ChangeDirectionType) {
    this.tabDirection = direction;
    setTimeout(() => {
      this.tabDirection = undefined;
    }, 150);
  }

  private computeFieldToFocus(
    row: number,
    col: BedCountTypes,
    dir?: ChangeDirectionType | undefined
  ): { row: number; col: number } {
    const fieldsInRow = 2; // Number of fields in each row (Operational and Occupied)
    const totalFields = (this.rowsWithFundedBeds()?.length ?? 0) * fieldsInRow; // Total number of fields in the 2D grid

    if (!totalFields) return { row: 0, col: 0 }; // No fields to navigate, this code should not be executing

    const currentPosition = row * fieldsInRow + col; // Calculate the current position in the 2D grid

    let nextPosition = currentPosition + (dir || 0);

    // Handle wrapping
    if (nextPosition < 0) {
      nextPosition += totalFields;
    } else if (nextPosition >= totalFields) {
      nextPosition -= totalFields;
    }

    const targetRow = Math.floor(nextPosition / fieldsInRow);
    const targetField = nextPosition % fieldsInRow;

    return {
      row: targetRow,
      col: targetField,
    };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRowValidationStatus(index: number, hasError: boolean) {
    this.rowsValidationStatus.update((currentStatus) => {
      const newStatus = new Map(currentStatus);
      newStatus.set(index, hasError);
      return newStatus;
    });
  }

  get facilitySmokingPolicyYesLabel() {
    return '<strong>Yes</strong>, we have a documented smoking policy.';
  }

  get facilitySmokingPolicyNoLabel() {
    return "<strong>No</strong>, we don't have a smoking policy, but we are creating one.";
  }
}
